import React, { useState, useRef } from "react";
import {
  makeStyles,
  Divider,
  Grid,
  Typography,
  Button,
  Menu,
  MenuItem,
} from "@material-ui/core";

import { useMunicipalContext } from "contexts/MunicipalContext";
import { useSnackbar } from "contexts/SnackbarContext";
import { displayTypes, tableDefaultActions } from "constants/amoTableConstants";
import { bankInformationStatuses } from "constants/amoTableDropdownOptions";
import { errorMessages } from "constants/errorMessages";
import { formIds } from "constants/formContentManagement";
import { snackbarTypes } from "constants/snackbar";
import AmoTooltip from "components/AmoTooltip";
import BankingInformationDetailsModal from "components/amo/BankingInformationDetailsModal";
import BankingRequestVerificationModal from "components/amo/BankingRequestVerificationModal";
import BankingUnlockOrDenyModal from "components/amo/BankingUnlockOrDenyModal";
import AmoTableWithFilters from "components/table/AmoTableWithFilters";
import ListPageWrapper from "components/ListPageWrapper";
import { exportToExcel } from "utils/export";
import {
  useBankingInformationPlaceholdersHooks,
  useBankingInformationReviewsHooks,
} from "./bankingInformationReviewsHooks";
import { useWrapApi } from "hooks/wrapApiHook";
import * as bankingInformationService from "api/services/bankingInformationService";
import { getZipFileByIdsAsync, remove } from "api/services/fileService";
import * as reviewService from "api/services/reviewsService";
import { useFormManagement } from "hooks/formManagementHook";
import { useGlobalStyles } from "hooks/globalStylesHook";
import { useIsMounted } from "hooks/useIsMounted";

const useStyles = makeStyles((theme) => ({
  button: {
    width: "20.563rem",
    height: "3.125rem",
  },
  listButton: {
    width: "11.125rem",
    height: "3.125rem",
  },
  dividerContainer: {
    paddingBottom: "0.688rem !important",
  },
  lightGreen: {
    color: "#00B628",
  },
}));

/**
 * The Banking Information Review List Page.
 *
 * @returns {React.Component} Banking Information Review List Page component.
 */
const BankingInformationReviewsListPage = () => {
  const { formFields } = useFormManagement(formIds.bankingInformation);

  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const globalClasses = useGlobalStyles();
  const mounted = useIsMounted();
  const { getMunicipalitiesNames } = useMunicipalContext();

  const [showModal, setShowModal] = useState(false);
  const [showUnlockOrDenyModal, setShowUnlockOrDenyModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentBankingInformation, setCurrentBankingInformation] = useState(
    false
  );
  const [focusedItem, setFocusedItem] = useState({});
  const [requestFilesIsOngoing, setRequestFilesIsOngoing] = useState(false);
  const rowRefs = useRef({});
  const filteredData = useRef([]);

  const openModal = (municipalityId) => {
    setShowDetailsModal(true);
    setCurrentBankingInformation(
      bankingInformationReviews.filter(
        (item) => item.municipalityId === municipalityId
      )[0]
    );
    setFocusedItem(null);
  };

  const openShowUnlockOrDenyModal = (municipalityId) => {
    setShowUnlockOrDenyModal(true);
    setCurrentBankingInformation(
      bankingInformationReviews.filter(
        (item) => item.municipalityId === municipalityId
      )[0]
    );
    setFocusedItem(null);
  };

  const unlockBankingInfo = useWrapApi(
    reviewService.unlock,
    "Successfully unlocked"
  );

  const denyUnlockBankingInfo = useWrapApi(
    reviewService.denyUnlock,
    "Successfully deny unlock"
  );

  const approveBankingInfo = useWrapApi(
    reviewService.approve,
    "Changes approved"
  );

  const rejectBankingInfo = useWrapApi(
    reviewService.reject,
    "Changes rejected"
  );

  const removeAssociatedFile = useWrapApi(remove);

  const unlock = async (id, fileId) => {
    if (id) {
      if (fileId) {
        await removeAssociatedFile.call(fileId, id);
      }
      await unlockBankingInfo.call(id);
      bankingInformationReviewsRefetch();
    }
  };

  const denyUnlock = async (id) => {
    if (id) {
      await denyUnlockBankingInfo.call(id);
      bankingInformationReviewsRefetch();
    }
  };

  const approve = async (id) => {
    if (id) {
      await approveBankingInfo.call(id);
      bankingInformationReviewsRefetch();
    }
  };

  const reject = async (id, fileId) => {
    if (id) {
      if (fileId) {
        await removeAssociatedFile.call(fileId, id);
      }
      await rejectBankingInfo.call(id);
      bankingInformationReviewsRefetch();
    }
  };

  const tableColumns = [
    {
      fieldKey: "municipalityName",
      label: "Municipality",
      widthPerc: 45,
      customRender: ({ municipalityId, municipalityName }) => (
        <Grid
          container
          direction="row"
          alignContent="center"
          alignItems="center"
        >
          <Grid item>
            <Button
              color="primary"
              onClick={() => openModal(municipalityId)}
              className={globalClasses.tableActionCell}
              ref={(element) => {
                rowRefs.current[municipalityId] = element;
              }}
              onFocus={() => setFocusedItem({ municipalityId })}
              disableRipple
            >
              {municipalityName}
            </Button>
          </Grid>
        </Grid>
      ),
      isCustom: true,
      dropdownOptions: getMunicipalitiesNames(),
      displayType: displayTypes.dropdown,
    },
    {
      fieldKey: "status",
      label: "Status",
      widthPerc: 35,
      customRender: ({ status }) => (
        <Typography
          className={
            [
              bankInformationStatuses.Verified,
              bankInformationStatuses.Changed,
            ].includes(status)
              ? classes.lightGreen
              : ""
          }
        >
          {status}
        </Typography>
      ),
      isCustom: true,
      dropdownOptions: bankInformationStatuses,
      displayType: displayTypes.dropdown,
    },
    {
      fieldKey: "actions",
      hideLabel: true,
      hideSearch: true,
      customRender: ({ id, status, municipalityId, fileId }) => {
        switch (status) {
          case bankInformationStatuses["Pending Review"]:
            return (
              <Button
                aria-label="tableActionSearch"
                onClick={() => openModal(municipalityId)}
                color="primary"
                size="medium"
                variant="contained"
                className={classes.listButton}
                disabled={showDetailsModal}
              >
                Review
              </Button>
            );
          case bankInformationStatuses["Unlock Requested"]:
            return (
              <Button
                aria-label="tableActionSearch"
                onClick={() => openShowUnlockOrDenyModal(municipalityId)}
                color="primary"
                size="medium"
                variant="outlined"
                className={classes.listButton}
                disabled={showUnlockOrDenyModal}
              >
                Unlock or Deny
              </Button>
            );
          default:
            return (
              <Button
                aria-label="tableActionSearch"
                onClick={() => unlock(id, fileId)}
                color="primary"
                size="medium"
                variant="outlined"
                className={classes.listButton}
                disabled={
                  status === bankInformationStatuses.Unverified ||
                  status === bankInformationStatuses["Pending Review"] ||
                  !id ||
                  showUnlockOrDenyModal
                }
              >
                Unlock
              </Button>
            );
        }
      },
      widthPerc: 15,
      isCustom: true,
    },
  ];

  const {
    data: bankingInformationReviews,
    isFetching: bankingInformationReviewsFetching,
    refetch: bankingInformationReviewsRefetch,
  } = useBankingInformationReviewsHooks();

  const {
    data: bankingInformationPlaceholders,
  } = useBankingInformationPlaceholdersHooks();

  const { call: putExportedBankingInformation } = useWrapApi(
    bankingInformationService.putExportedBankingInformation
  );

  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const ellipsisOnClick = (event) => setEllipsisAnchorEl(event.currentTarget);
  const ellipsisOnClose = () => setEllipsisAnchorEl(null);

  const exportData = async () => {
    try {
      const data = bankingInformationReviews;
      const columns = [
        { fieldKey: "municipalityId", label: "Municipal ID" },
        ...tableColumns.filter(({ fieldKey }) => fieldKey !== "actions"),
        { fieldKey: "treasurerName", label: "Treasurer" },
        { fieldKey: "contactName", label: "Contact Name" },
        { fieldKey: "contactEmail", label: "Contact Email" },
        { fieldKey: "contactPhone", label: "Contact Phone" },
        { fieldKey: "phoneExtension", label: "Contact Phone Extension" },
        {
          fieldKey: "institutionName",
          label: "Institution Name",
        },
        {
          fieldKey: "institutionNumber",
          label: "Institution Number",
        },
        {
          fieldKey: "transitNumber",
          label: "Transit Number",
        },
        {
          fieldKey: "accountNumber",
          label: "Account Number",
        },
        {
          fieldKey: "updatedDate",
          label: "Updated Date",
        },
        {
          fieldKey: "isExported",
          label: "Already Exported?",
        },
      ];
      exportToExcel(data, columns, "Banking Information Reviews", {
        sheetName: "Reviews",
        addTimestamp: true,
        specialColumnSizes: { municipalityId: 10, municipalityName: 30 },
      });
      const isExportedMarked = await putExportedBankingInformation();
      if (isExportedMarked.data.status === 200) {
        bankingInformationReviewsRefetch();
      }
    } catch (error) {
      console.error(error);

      showSnackbar(errorMessages.creatingSpreadsheet, snackbarTypes.error);
    }
  };

  const downloadAllFiles = async () => {
    try {
      setRequestFilesIsOngoing(true);
      const filesIds = filteredData.current
        .filter(({ fileId }) => !!fileId)
        .map(({ fileId }) => fileId);
      if (filesIds.length === 0) {
        showSnackbar("No files found", snackbarTypes.warning);
        return;
      }
      const fileName = `Banking files_${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "_")}.zip`;
      const { data } = await getZipFileByIdsAsync(filesIds, fileName);

      if (!mounted.current) {
        return;
      }

      // Download file
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch {
      if (!mounted.current) {
        return;
      }

      showSnackbar(errorMessages.generic, snackbarTypes.error);
    } finally {
      if (mounted.current) {
        setRequestFilesIsOngoing(false);
      }
    }
  };

  const isPendingReviewItems = Boolean(
    bankingInformationReviews?.some(
      ({ status }) => status === bankInformationStatuses["Pending Review"]
    )
  );

  const requestVerificationButton = (
    <Button
      aria-label="tableActionSearch"
      onClick={() => {
        setShowModal(true);
        setFocusedItem(null);
      }}
      color="primary"
      size="medium"
      variant="contained"
      className={classes.button}
      disabled={bankingInformationReviewsFetching || isPendingReviewItems}
    >
      Request Verification
    </Button>
  );

  const customExportButton = (
    <Button
      aria-label="tableActionSearch"
      color="primary"
      size="medium"
      variant="outlined"
      className={classes.button}
      onClick={ellipsisOnClick}
      disabled={bankingInformationReviewsFetching}
    >
      Export Banking Information
    </Button>
  );

  const tableActions = {
    actions: [
      "requestVerification",
      "customExport",
      tableDefaultActions.search,
    ],
    customActionsComponents: {
      requestVerification: isPendingReviewItems ? (
        <AmoTooltip
          tooltipText="Process all items pending review to enable this button"
          customComponent={requestVerificationButton}
        />
      ) : (
        requestVerificationButton
      ),
      customExport: customExportButton,
    },
  };

  return (
    <ListPageWrapper>
      <Grid container direction="column" spacing={2} wrap="nowrap">
        <Grid item>
          <Typography variant="h1">Banking Information</Typography>
        </Grid>
        <Grid item className={classes.dividerContainer}>
          <Divider />
        </Grid>
        <Grid item>
          <Grid item>
            <AmoTableWithFilters
              items={bankingInformationReviews}
              columns={tableColumns}
              defaultOrderBy="municipalityName"
              onScroll={(item) => {
                rowRefs.current?.[item.municipalityId]?.focus();
              }}
              onFilteredDataChanged={(value) => {
                filteredData.current = value;
              }}
              focusedItem={focusedItem}
              actionsProps={tableActions}
              isLoading={bankingInformationReviewsFetching}
              uniqueKeyGenerationFields={["municipalityId"]}
            />
          </Grid>
        </Grid>
      </Grid>
      <BankingRequestVerificationModal
        showModal={showModal}
        closeModal={() => {
          setShowModal(false);
          setFocusedItem({});
        }}
        bankingInformationReviews={bankingInformationReviews}
        bankingInformationReviewsRefetch={bankingInformationReviewsRefetch}
        bankingInformationPlaceholders={bankingInformationPlaceholders}
      />
      <BankingUnlockOrDenyModal
        showModal={showUnlockOrDenyModal}
        closeModal={() => {
          setShowUnlockOrDenyModal(false);
          setFocusedItem({});
        }}
        onUnlockClick={unlock}
        onDenyUnlockClick={denyUnlock}
        currentBankingInformation={currentBankingInformation}
      />
      <BankingInformationDetailsModal
        showModal={showDetailsModal}
        closeModal={(shouldRefetch = false) => {
          setShowDetailsModal(false);
          setFocusedItem({
            municipalityId: currentBankingInformation.municipalityId,
          });
          if (shouldRefetch) {
            bankingInformationReviewsRefetch();
          }
        }}
        onApproveClick={approve}
        onRejectClick={reject}
        formFields={formFields}
        bankingInformation={currentBankingInformation}
      />
      <Menu
        anchorEl={ellipsisAnchorEl}
        open={!!ellipsisAnchorEl}
        onClose={ellipsisOnClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            ellipsisOnClose();
            exportData();
          }}
        >
          <Typography variant="body1">Export data</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            ellipsisOnClose();
            downloadAllFiles();
          }}
          disabled={requestFilesIsOngoing}
        >
          <Typography variant="body1">Export files</Typography>
        </MenuItem>
      </Menu>
    </ListPageWrapper>
  );
};

export default BankingInformationReviewsListPage;
